import { type Filter, type Query } from '@cubejs-client/core'
import { subMonths } from 'date-fns'
import { mergeDeepRight } from 'ramda'
import { useHistory } from 'react-router-dom'

import { createStore } from '@driftt/dds'
import { CubeQueryStringBuilder } from '@driftt/dds.reports'

export interface ISavedQuery {
  name: string
  query: Query | string
}

export interface ConversationDetailContextType {
  startDate: Date
  endDate: Date
  filters: Filter[]
}

export const DEFAULT_START_DATE = subMonths(new Date(), 1)
export const DEFAULT_END_DATE = new Date()

const {
  Provider: ConversationDetailStoreProvider,
  useStore: useConversationDetailStoreBase,
} = createStore<ConversationDetailContextType>({
  startDate: DEFAULT_START_DATE,
  endDate: DEFAULT_END_DATE,
  filters: [],
})

export { ConversationDetailStoreProvider }

export const useConversationDetailStore = () => {
  const {
    store,
    setStore: setStoreBase,
    updateStore: updateStoreBase,
  } = useConversationDetailStoreBase()

  const history = useHistory()

  const setQueryString = (
    newValues: Partial<ConversationDetailContextType>
  ) => {
    const {
      startDate: newStartDate,
      endDate: newEndDate,
      filters: newFilters,
    } = newValues

    const queryString = new CubeQueryStringBuilder({
      addQueryPrefix: true,
    })
      .withStartDate(newStartDate ?? store.startDate)
      .withEndDate(newEndDate ?? store.endDate)
      .withFilters(newFilters ?? store.filters)
      .build()

    history.push(queryString)
  }

  const setStore = (newValues: ConversationDetailContextType) => {
    setQueryString(newValues)
    setStoreBase(newValues)
  }

  const updateStore = (newValues: Partial<ConversationDetailContextType>) => {
    const updatedValues = mergeDeepRight(
      store,
      newValues
    ) as Partial<ConversationDetailContextType>

    setQueryString(updatedValues)
    updateStoreBase(newValues)
  }

  return {
    store,
    setStore,
    updateStore,
  }
}
